import { UploadOutlined } from '@ant-design/icons'
import { DatePicker, Upload } from 'antd'
import { subMonths } from 'date-fns'
import moment from 'moment'
import React, { useEffect, useState } from 'react'
import { useQuery } from 'react-query'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Button } from 'sutra/components/Button'
import { Radio } from 'sutra/components/Radio/Radio'
import { RadioGroup } from 'sutra/components/Radio/RadioGroup'

import { FormTitle } from 'features/project-form/components/FormTitle'
import { getSourcesFromFile } from 'features/project-form/features/csv/utils'
import { Product } from 'features/project-form/features/product/types'
import { ProjectSourceURL } from 'features/project-form/features/source/types'
import { useProjectFormService } from 'features/project-form/service'
import { useSourceTypes } from 'features/project-form/utils/source-types'

type DatePreset = 'last_12_months' | 'oldest_source_date' | 'custom'

type Props = {
  reservedKeywords?: string[]
  onSuccess: (s: { sources: ProjectSourceURL[]; products: Product[] }) => void
  projectId?: string
}

const getLast12MonthsDate = (): Date => {
  return subMonths(new Date(), 12)
}

export const CsvForm: React.FC<Props> = ({
  reservedKeywords = [],
  onSuccess,
  projectId,
}) => {
  const [file, setFile] = useState<File | null>(null)
  const [sourceDate, setSourceDate] = useState<Date>(getLast12MonthsDate())
  const [datePreset, setDatePreset] = useState<DatePreset>('last_12_months')
  const [error, setError] = useState<string | null>(null)
  const projectFormService = useProjectFormService()
  const { data: sourceTypes } = useSourceTypes()

  const { data: oldestFromDate } = useQuery(
    ['oldest-source-from-date', projectId],
    () => projectFormService.getOldestSourceFromDate(projectId!),
    {
      enabled: !!projectId,
      refetchOnWindowFocus: false,
    },
  )

  useEffect(() => {
    if (datePreset === 'last_12_months') {
      setSourceDate(getLast12MonthsDate())
    } else if (datePreset === 'oldest_source_date' && oldestFromDate) {
      setSourceDate(new Date(oldestFromDate))
    }
  }, [datePreset, oldestFromDate])

  useEffect(() => {
    if (file && sourceTypes) {
      getSourcesFromFile(
        file,
        reservedKeywords,
        sourceDate,
        sourceTypes,
        (response) => {
          if (typeof response === 'string') {
            setError(response)
          } else {
            onSuccess(response)
          }
        },
      )
    }
  }, [file, onSuccess, reservedKeywords, sourceDate, sourceTypes])

  return (
    <div>
      <FormTitle>Auto-Fill Products and Sources</FormTitle>
      <Center>
        <p>
          Please select a CSV file to auto-fill the project creation
          information.
          <br /> Reach out to Yogi Support for an example file. Certain rules
          must be followed to load the data correctly.
          <br /> You can review the pulled information after you upload the
          file.
        </p>
        <p>Select default sources start date.</p>
        <DatePresetGroup
          aria-label="Default sources start date"
          value={datePreset}
          onValueChange={setDatePreset}
        >
          <Radio value="last_12_months">Last 12 months</Radio>
          {projectId && (
            <Radio
              value="oldest_source_date"
              disabled={!oldestFromDate}
              title={
                !oldestFromDate
                  ? 'No existing sources with a start date found'
                  : undefined
              }
            >
              Oldest source date
            </Radio>
          )}
          <Radio value="custom">Custom</Radio>
        </DatePresetGroup>
        {datePreset === 'custom' ? (
          <DatePicker
            defaultValue={moment(sourceDate)}
            onChange={(value) => {
              if (value) {
                setSourceDate(value.toDate())
              }
            }}
            format={'MM/DD/YYYY'}
          />
        ) : (
          <DateDisplay>{moment(sourceDate).format('MM/DD/YYYY')}</DateDisplay>
        )}
        <br />
        <br />
        <Flex>
          <Upload
            accept={'.csv'}
            beforeUpload={() => false}
            onChange={(info) => {
              const fileList = info.fileList as Array<{
                size: number
                originFileObj?: File
              }>
              if (fileList.length > 0) {
                const lastFile = fileList[fileList.length - 1]!
                if (lastFile.size / 1000 / 1000 >= 600) {
                  toast.error('The uploaded file should be less than 600 MB')
                  return
                }
                setFile(lastFile.originFileObj ?? null)
              } else {
                setFile(null)
              }
            }}
          >
            <Button variant="secondary">
              <UploadOutlined /> Click to Upload
            </Button>
          </Upload>
        </Flex>
      </Center>

      {file && error && <Error>{error}</Error>}
    </div>
  )
}

const DatePresetGroup = styled(RadioGroup<DatePreset>)`
  justify-content: center;
  margin-bottom: 12px;
`

const Flex = styled.div`
  display: flex;
  justify-content: center;
`

const Center = styled.div`
  text-align: center;
`
const Error = styled.div`
  background-color: #f9f9f9;
  min-height: 10px;
  max-height: 150px;
  overflow: auto;
  margin: 20px;
  text-align: center;
  color: #ff4d4f;
  white-space: pre-line;
`
const DateDisplay = styled.span`
  font-weight: 500;
  color: #555;
`
